import { Component, EventEmitter, HostListener, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

const STORAGE_KEY   = 'chatbot-fab-pos';
const FAB_SIZE      = 64;
const DRAG_THRESHOLD = 5;
const EDGE_MARGIN   = 16;

@Component({
  selector: 'mindbase-chatbot-fab',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './chatbot-fab.component.html',
  styleUrl: './chatbot-fab.component.scss',
})
export class ChatbotFabComponent implements OnInit {
  @Output() fabClick = new EventEmitter<void>();

  readonly botAvatarUrl = 'assets/images/icon-chat.svg';

  protected posX = 0;
  protected posY = 0;
  protected isDragging = false;

  private dragActive   = false;
  private hasDragged   = false;
  private startPointerX = 0;
  private startPointerY = 0;
  private startPosX    = 0;
  private startPosY    = 0;

  ngOnInit(): void {
    this.loadPosition();
  }

  // ── Position persistence ──────────────────────────────────────────────────

  private loadPosition(): void {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { x, y } = JSON.parse(saved) as { x: number; y: number };
        this.posX = x;
        this.posY = y;
        this.clamp();
        return;
      }
    } catch { /* ignore */ }
    // Default: bottom-right corner
    this.posX = window.innerWidth  - FAB_SIZE - 32;
    this.posY = window.innerHeight - FAB_SIZE - 32;
  }

  private savePosition(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ x: this.posX, y: this.posY }));
    } catch { /* ignore */ }
  }

  private clamp(): void {
    this.posX = Math.max(EDGE_MARGIN, Math.min(this.posX, window.innerWidth  - FAB_SIZE - EDGE_MARGIN));
    this.posY = Math.max(EDGE_MARGIN, Math.min(this.posY, window.innerHeight - FAB_SIZE - EDGE_MARGIN));
  }

  // ── Pointer / drag handlers ───────────────────────────────────────────────

  onPointerDown(event: PointerEvent): void {
    this.dragActive    = true;
    this.hasDragged    = false;
    this.startPointerX = event.clientX;
    this.startPointerY = event.clientY;
    this.startPosX     = this.posX;
    this.startPosY     = this.posY;
    // Route all future pointer events to this element even outside viewport
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.dragActive) return;
    const dx = event.clientX - this.startPointerX;
    const dy = event.clientY - this.startPointerY;
    if (!this.hasDragged && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

    this.hasDragged = true;
    this.isDragging = true;
    this.posX = this.startPosX + dx;
    this.posY = this.startPosY + dy;
    this.clamp();
  }

  onPointerUp(_event: PointerEvent): void {
    this.dragActive = false;
    this.isDragging = false;
    if (this.hasDragged) {
      this.savePosition();
    }
  }

  onClick(): void {
    if (!this.hasDragged) {
      this.fabClick.emit();
    }
  }

  // ── Re-clamp when viewport resizes ───────────────────────────────────────

  @HostListener('window:resize')
  onResize(): void {
    this.clamp();
    this.savePosition();
  }
}
